import { useApolloClient } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useToast } from '../../components/Toast';
import { WEBSOCKET_URL } from '../../config/websocketConfig';
import {
  type Recommendation,
  type ReplyNotificationData,
  type ScoreUpdateData,
  type SocketContextInterface,
  type WebSocketMessage,
} from '../../interfaces/websocket.interface';
import { useAuth } from '../AuthContext';
import { EventBus } from '../EventBus';
import { registerModerationHandlers } from './moderation';

// — Connection tunables —
const RECONNECT_MAX_ATTEMPTS = 5;
const RECONNECT_MAX_DELAY_MS = 30_000;
const PING_INTERVAL_MS = 30_000;
const AUTH_DELAY_MS = 500;

type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'authenticated'
  | 'error';

const SocketContext = createContext<SocketContextInterface>({
  onScoreUpdate: () => () => {},
  scoreUpdates: null,
  seedScores: () => {},
  socket: null,
  connected: false,
  authenticated: false,
  newRecommendations: [],
  unreadCount: 0,
  connectionStatus: 'disconnected',
  clearNewRecommendations: () => {},
  reconnect: () => {},
  sendMessage: () => {},
  lastError: null,
  connectWebSocket: () => {},
  disconnectWebSocket: () => {},
  onReplyNotification: () => () => {},
  onSharedStoryNotification: () => () => {},
  onInteractionUpdate: () => () => {},
  onEvent: () => () => {},
});

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  // — UI state (drives re-renders for consumers) —
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const [newRecommendations, setNewRecommendations] = useState<
    Recommendation[]
  >([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scoreUpdates, setScoreUpdates] = useState<
    Map<string, ScoreUpdateData>
  >(new Map());
  // Bumped to manually trigger a reconnect from the imperative API
  const [reconnectTrigger, setReconnectTrigger] = useState(0);

  // — Imperative state (no re-renders) —
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const eventBusRef = useRef(new EventBus());

  // — Latest values for handlers; updated via sync effect so the connection
  //   effect doesn't have to re-run when user object reference changes —
  const userIdRef = useRef<string | null>(null);
  const sessionRef = useRef<string | null>(null);
  const userInfoRef = useRef<{ username?: string; email?: string }>({});

  // TRIL-297: the connection identity is the stable JWT user from AuthContext,
  // NOT the Redux userStore mirror. The mirror transiently blips falsy on some
  // navigations (without a logout) — see TRIL-304 — which used to tear the
  // socket down and park status at 'disconnected' (Offline until re-login).
  // AuthContext's user only clears on a real sign-out, so the socket survives
  // Redux churn.
  const { isSignedIn, session, user: authUser } = useAuth();
  const apolloClient = useApolloClient();
  const { showToast } = useToast();

  // Sync the latest user/session into refs so handlers always see fresh values
  // without needing to be re-created (which would tear down the WS).
  useEffect(() => {
    userIdRef.current = authUser?.id ?? null;
    sessionRef.current = (session as string | null) ?? null;
    userInfoRef.current = {
      username: authUser?.username,
      email: authUser?.email,
    };
  }, [authUser?.id, authUser?.username, authUser?.email, session]);

  useEffect(() => {
    return registerModerationHandlers(eventBusRef.current, {
      currentUserId: authUser?.id ?? null,
      apolloClient,
      showToast,
    });
  }, [authUser?.id, apolloClient, showToast]);

  // — Stable subscription API; never changes reference —
  const onEvent = useCallback(
    (type: string, callback: (data: any) => void) =>
      eventBusRef.current.on(type, callback),
    [],
  );
  const onScoreUpdate = useCallback(
    (callback: (data: ScoreUpdateData) => void) =>
      eventBusRef.current.on('STORY_SCORE_UPDATED', callback),
    [],
  );
  // Seed baseline scores from getUserFeed (storyAnalyzer.overall_score) so
  // newly published posts render with their real initial score instead of 0
  // before any STORY_SCORE_UPDATED event arrives. Existing entries are never
  // overwritten — live WS updates always win.
  const seedScores = useCallback(
    (entries: Array<{ storyId: string; score: number }>) => {
      if (!entries.length) return;
      setScoreUpdates((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const { storyId, score } of entries) {
          if (!storyId || typeof score !== 'number') continue;
          const existing = next.get(storyId);
          if (existing && existing.newScore === score) continue;
          const previousScore = existing?.newScore ?? score;
          const change = score - previousScore;
          next.set(storyId, {
            storyId,
            newScore: score,
            previousScore,
            scoreDelta: {
              original_score: previousScore,
              new_score: score,
              change,
              change_direction:
                change > 0 ? 'up' : change < 0 ? 'down' : 'unchanged',
            },
            timestamp: Date.now(),
          });
          changed = true;
        }
        return changed ? next : prev;
      });
    },
    [],
  );
  const onReplyNotification = useCallback(
    (callback: (data: ReplyNotificationData) => void) =>
      eventBusRef.current.on('new_replies', callback),
    [],
  );
  const onSharedStoryNotification = useCallback(
    (callback: () => void) =>
      eventBusRef.current.on('new_shared_story', callback),
    [],
  );
  const onInteractionUpdate = useCallback(
    (callback: (data: any) => void) =>
      eventBusRef.current.on('INTERACTION_UPDATED', callback),
    [],
  );

  const clearNewRecommendations = useCallback(() => {
    setNewRecommendations([]);
    setUnreadCount(0);
  }, []);

  // sendMessage reads wsRef directly so its identity is stable across renders —
  // no need for a `socket` state value in the context API.
  const sendMessage = useCallback((message: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(message));
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        setLastError(`Failed to send message: ${error}`);
      }
    } else {
      setLastError('WebSocket not connected');
    }
  }, []);

  // — Single connection effect. Deps are STABLE values only:
  //   isSignedIn (boolean), authUser?.id (JWT id), session (string),
  //   reconnectTrigger. Identity is the AuthContext JWT, not the Redux user
  //   mirror (TRIL-297), so Redux churn cannot tear the socket down.
  useEffect(() => {
    if (!isSignedIn || !authUser?.id || !session) {
      // Nothing to connect to. Cleanup runs when deps change away from this.
      return;
    }

    let cancelled = false;
    intentionalCloseRef.current = false;

    const handleMessage = (raw: string) => {
      let message: WebSocketMessage;
      try {
        message = JSON.parse(raw);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error, raw);
        setLastError('Failed to parse message from server');
        return;
      }

      switch (message.type) {
        case 'connected':
          setConnectionStatus('connected');
          break;

        case 'auth_success':
        case 'authenticated':
          setConnectionStatus('authenticated');
          reconnectAttemptsRef.current = 0;
          setLastError(null);
          break;

        case 'auth_error':
          setConnectionStatus('connected');
          setLastError(
            `Authentication failed: ${message.data?.message || 'Unknown error'}`,
          );
          break;

        case 'NEW_RECOMMENDATIONS_AVAILABLE': {
          const recommendation = message.data as Recommendation;
          setNewRecommendations((prev) => {
            if (
              recommendation?.postId &&
              prev.some((r) => r.postId === recommendation.postId)
            ) {
              return prev;
            }
            return [recommendation, ...prev];
          });
          setUnreadCount((prev) => prev + 1);
          if (
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            new Notification('New content available', {
              body: 'There is new content in your feed.',
              icon: '/favicon.ico',
              tag: recommendation?.postId
                ? `recommendation-${recommendation.postId}`
                : undefined,
              silent: false,
            });
          }
          break;
        }

        case 'STORY_SCORE_UPDATED': {
          const { type, ...scoreData } = message;
          setScoreUpdates((prev) => {
            const next = new Map(prev);
            next.set((scoreData as any).storyId, scoreData as ScoreUpdateData);
            return next;
          });
          eventBusRef.current.emit('STORY_SCORE_UPDATED', scoreData);
          break;
        }

        case 'ping': {
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'pong', timestamp: Date.now() }));
          }
          break;
        }

        case 'pong':
          break;

        case 'new_replies':
          eventBusRef.current.emit(
            'new_replies',
            message.data as ReplyNotificationData,
          );
          break;

        case 'new_shared_story':
          eventBusRef.current.emit('new_shared_story', message.data);
          break;

        case 'report_assigned':
          // TRIL-82: live moderator-queue refresh. The handler in
          // moderation.ts filters to the current user.
          eventBusRef.current.emit('report_assigned', message.data);
          break;

        case 'report_resolved':
          eventBusRef.current.emit('report_resolved', message.data);
          break;

        case 'session_revoked':
          // TRIL-385: real-time cross-device logout. The guard in
          // useSessionRevocationGuard filters by this browser's own sessionId.
          eventBusRef.current.emit('session_revoked', message.data);
          break;

        case 'direct_message':
          // TRIL-394: live 1:1 message delivery. useConversation subscribes and
          // appends to the open thread / bumps the inbox.
          eventBusRef.current.emit('direct_message', message.data);
          break;

        case 'dm_typing':
          // TRIL-394: ephemeral typing indicator for the open conversation.
          eventBusRef.current.emit('dm_typing', message.data);
          break;

        case 'community_reports_updated':
          eventBusRef.current.emit('community_reports_updated', message.data);
          break;

        case 'vouch_received':
          // TRIL-400: notice to a vouch recipient. Subscribers: the /vouch
          // received-list refetch (useReceivedVouches) + the app-level toast
          // (useVouchReceivedToast).
          eventBusRef.current.emit('vouch_received', message.data);
          break;

        case 'post_status_changed':
          eventBusRef.current.emit('post_status_changed', message.data);

          // Issue B: if the broadcast carries a score (republish does), update
          // the scoreUpdates Map so feed cards re-render with the live score.
          // The Map's value takes precedence over Redux via `??` in the feed
          // card renderer, so without this the old seeded score persists.
          if (typeof message.data?.score === 'number' && message.data?.postId) {
            setScoreUpdates((prev) => {
              const previousScore =
                prev.get(message.data.postId)?.newScore ?? message.data.score;
              const newScore = message.data.score as number;
              const change = newScore - previousScore;
              const next = new Map(prev);
              next.set(message.data.postId, {
                storyId: message.data.postId,
                newScore,
                previousScore,
                scoreDelta: {
                  original_score: previousScore,
                  new_score: newScore,
                  change,
                  change_direction:
                    change > 0 ? 'up' : change < 0 ? 'down' : 'unchanged',
                },
                timestamp: Date.now(),
              });
              return next;
            });
          }
          break;

        case 'reputation.updated':
          // TRIL-66 Phase 3c: server worker now emits this. UI consumers
          // (toast on own-user session, silent updates on third-party cards)
          // wire up via onEvent('reputation.updated', ...) in Phase 5.
          // Emitting from here lets future subscribers register without
          // touching this dispatcher again.
          eventBusRef.current.emit('reputation.updated', message.data);
          break;

        case 'INTERACTION_UPDATED':
          eventBusRef.current.emit('INTERACTION_UPDATED', message);
          break;

        case 'subscribed_to_replies':
          break;

        case 'error':
          setLastError(message.data?.message || 'WebSocket error');
          break;

        default:
          console.log('Unknown WebSocket message type:', message.type, message);
      }
    };

    const connect = () => {
      if (cancelled) return;

      const ws = new WebSocket(WEBSOCKET_URL);
      wsRef.current = ws;
      setConnectionStatus('connecting');
      setLastError(null);

      ws.onopen = () => {
        if (wsRef.current !== ws) {
          ws.close(1000, 'Superseded');
          return;
        }
        setConnectionStatus('connected');

        // Authenticate after a short delay so the server has finished any
        // post-connect bookkeeping. Read the latest token from refs so we
        // don't capture a stale session from when the effect first ran.
        setTimeout(() => {
          if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
          ws.send(
            JSON.stringify({
              action: 'authenticate',
              userId: userIdRef.current,
              token: sessionRef.current,
              userInfo: userInfoRef.current,
            }),
          );
        }, AUTH_DELAY_MS);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        handleMessage(event.data);
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        setConnectionStatus('disconnected');

        if (intentionalCloseRef.current || event.code === 1000) {
          return;
        }

        if (reconnectAttemptsRef.current < RECONNECT_MAX_ATTEMPTS) {
          const attempt = reconnectAttemptsRef.current;
          const delay = Math.min(1000 * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
          reconnectAttemptsRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
          }, delay);
        } else {
          setConnectionStatus('error');
          setLastError('Max reconnect attempts reached');
        }
      };

      ws.onerror = () => {
        if (wsRef.current !== ws) return;
        setLastError('Connection error');
      };
    };

    connect();

    return () => {
      cancelled = true;
      intentionalCloseRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      const ws = wsRef.current;
      if (ws) {
        wsRef.current = null;
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close(1000, 'Cleanup');
        }
      }

      reconnectAttemptsRef.current = 0;
      setConnectionStatus('disconnected');
    };
  }, [isSignedIn, authUser?.id, session, reconnectTrigger]);

  // Clear notification-related state when auth drops — implicit (token
  // expiry, session invalidation) or explicit (logout button). Without this,
  // a stale `unreadCount` / `newRecommendations` / `lastError` could leak
  // into the next user's session and trigger spurious toasts/refreshes.
  useEffect(() => {
    if (!isSignedIn || !authUser?.id) {
      setNewRecommendations([]);
      setUnreadCount(0);
      setLastError(null);
      setScoreUpdates(new Map());
    }
  }, [isSignedIn, authUser?.id]);

  // Ping interval — separate concern, only runs while authenticated.
  useEffect(() => {
    if (connectionStatus !== 'authenticated') return;
    const id = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, PING_INTERVAL_MS);
    pingIntervalRef.current = id;
    return () => {
      clearInterval(id);
      if (pingIntervalRef.current === id) pingIntervalRef.current = null;
    };
  }, [connectionStatus]);

  // Notification permission — request once on mount.
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);

  // — Imperative API for explicit user actions —
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setLastError(null);
    setReconnectTrigger((n) => n + 1);
  }, []);

  // TRIL-196: browser-online listener. After 5 reconnect attempts
  // (~62s of exponential backoff) the WS state machine parks at
  // status='error' with no automatic recovery — users get stuck
  // showing 'Offline' even after the network is restored. When the
  // browser detects online and the WS isn't currently authenticated,
  // kick a fresh reconnect via the existing imperative API. Guards
  // make this a no-op when already healthy or not signed in.
  useEffect(() => {
    const handleOnline = () => {
      if (connectionStatus !== 'authenticated' && isSignedIn && authUser?.id) {
        reconnect();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [connectionStatus, isSignedIn, authUser?.id, reconnect]);

  // Used by the logout button. The full teardown also happens automatically
  // when isSignedIn flips to false (the connection effect cleans up).
  const disconnectWebSocket = useCallback(async () => {
    intentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    const ws = wsRef.current;
    if (ws) {
      wsRef.current = null;
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, 'User logout');
      }
    }
    setNewRecommendations([]);
    setUnreadCount(0);
    setConnectionStatus('disconnected');
    setLastError(null);
  }, []);

  // Kept in the public API for backwards compatibility but is now a no-op —
  // the connection effect manages the lifecycle automatically based on auth.
  const connectWebSocket = useCallback(() => {
    setReconnectTrigger((n) => n + 1);
  }, []);

  // Derived booleans so consumers don't need to know the status enum.
  const connected =
    connectionStatus === 'connected' || connectionStatus === 'authenticated';
  const authenticated = connectionStatus === 'authenticated';

  const contextValue: SocketContextInterface = {
    socket: wsRef.current, // exposed for backwards compat; should not be used
    connected,
    authenticated,
    connectionStatus,
    lastError,
    newRecommendations,
    unreadCount,
    clearNewRecommendations,
    reconnect,
    sendMessage,
    connectWebSocket,
    disconnectWebSocket,
    scoreUpdates,
    onScoreUpdate,
    seedScores,
    onReplyNotification,
    onSharedStoryNotification,
    onInteractionUpdate,
    onEvent,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const useWebSocketStatus = () => {
  const { connected, authenticated, connectionStatus, lastError } = useSocket();
  return {
    connected,
    authenticated,
    status: connectionStatus,
    lastError,
    isHealthy: connectionStatus === 'authenticated' && !lastError,
  };
};

export const useWebSocketLogout = () => {
  const { disconnectWebSocket } = useSocket();
  return { disconnectWebSocket };
};

export const useRecommendations = () => {
  const { newRecommendations, clearNewRecommendations, unreadCount } =
    useSocket();

  const getLatestRecommendation = useCallback(() => {
    return newRecommendations[0] || null;
  }, [newRecommendations]);

  const getRecommendationsByCategory = useCallback(
    (category: string) =>
      newRecommendations.filter((rec) => rec.categories?.includes(category)),
    [newRecommendations],
  );

  const markAsRead = useCallback((postId: string) => {
    console.log('Marking recommendation as read:', postId);
  }, []);

  return {
    recommendations: newRecommendations,
    unreadCount,
    latestRecommendation: getLatestRecommendation(),
    getRecommendationsByCategory,
    markAsRead,
    clearRecommendations: clearNewRecommendations,
  };
};
